import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';

import { ApiClientService } from '@tablesready/api-client';
import {
  AuthAccountService,
  AuthLocationService,
  SocketService,
} from '@tablesready/frontend-store';
import { PricePlansRestrictionsService } from '@tablesready/frontend-restrictions';
import { WebSocketEventsEnum } from '@shared/websocket';
import { LocationBadgeCounts, LocationBadgesResponse } from '@shared/location/models';
import { isOnFreePlan } from '@shared/user/functions';

@Injectable({ providedIn: 'root' })
export class LocationSwitcherBadgeService {
  private readonly badgeCounts$ = new BehaviorSubject<Map<string, LocationBadgeCounts>>(new Map());
  private subscriptions = new Subscription();
  private isInitialized = false;

  readonly badges$ = this.badgeCounts$.asObservable();

  constructor(
    private readonly http: ApiClientService,
    private readonly socketService: SocketService,
    private readonly locationService: AuthLocationService,
    private readonly accountService: AuthAccountService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
  ) {}

  init(locationIds: string[]): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    const loadSub = this.loadBatchCounts(locationIds).subscribe();
    this.subscriptions.add(loadSub);
    this.listenToWebSocketEvents();
  }

  destroy(): void {
    this.isInitialized = false;
    this.badgeCounts$.next(new Map());
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
  }

  getBadgeCount$(locationId: string): Observable<number> {
    return this.badges$.pipe(
      map(badges => {
        const counts = badges.get(locationId);
        const location = this.locationService.getById(locationId);
        const account = location ? this.accountService.getById(location.accountId) : undefined;

        if (
          !counts
          || !location
          || !account
          || isOnFreePlan({ account, location })
          || !account.features?.notificationCenter
          || !location.messages?.notificationCenter
        ) {
          return 0;
        }

        const isBusiness = this.pricePlansRestrictionsService
          .availableForBusinessPlan(account.subscriptionType, location.recurly);

        return isBusiness ? counts.total : counts.messages;
      })
    );
  }

  refreshCounts(locationIds: string[]): Observable<LocationBadgesResponse> {
    return this.loadBatchCounts(locationIds);
  }

  private loadBatchCounts(locationIds: string[]): Observable<LocationBadgesResponse> {
    if (!locationIds.length) {
      return of({});
    }

    return this.http.get<LocationBadgesResponse>(
      `/v.1/parties/notifications/unread-counts-batch?locationIds=${locationIds.join(',')}`
    ).pipe(
      tap(response => {
        const newCounts = new Map(this.badgeCounts$.value);

        Object.entries(response).forEach(([locationId, counts]) => {
          newCounts.set(locationId, counts);
        });

        this.badgeCounts$.next(newCounts);
      }),
      catchError(() => of({}))
    );
  }

  private listenToWebSocketEvents(): void {
    const badgeUpdateSub = this.socketService
      .getEventStream(WebSocketEventsEnum.locationBadgeUpdate)
      .subscribe((event: any) => {
        this.updateBadgeCount(event.locationId, event.type, event.delta);
      });

    this.subscriptions.add(badgeUpdateSub);
  }

  private updateBadgeCount(
    locationId: string,
    type: 'message' | 'notification',
    delta: number
  ): void {
    if (!Number.isFinite(delta)) {
      return;
    }

    const current = this.badgeCounts$.value;
    const locationBadge = current.get(locationId) || { messages: 0, notifications: 0, total: 0 };

    if (type === 'message') {
      locationBadge.messages = Math.max(0, locationBadge.messages + delta);
    }
    if (type === 'notification') {
      locationBadge.notifications = Math.max(0, locationBadge.notifications + delta);
    }

    locationBadge.total = locationBadge.messages + locationBadge.notifications;

    current.set(locationId, locationBadge);
    this.badgeCounts$.next(new Map(current));
  }
}
